import { holdsPermission, type Principal } from '../../session/permissions.js';

/**
 * The workspace's four areas, and the permission each one is for.
 *
 * One list, read by the navigation, the command palette and the route guards, so that a person who
 * can reach an area by typing its address is exactly the person who can see its link. Three copies
 * of this list would be three chances for them to disagree, and the disagreement a user notices is
 * the one where the link is missing and the page works.
 *
 * The permission is the *navigational* one — held anywhere, over any queue or team — because a link
 * names no queue. What each area then does is authorised per resource, by the API.
 */
export interface WorkspaceArea {
  readonly id: 'agent' | 'tickets' | 'supervise' | 'admin';
  readonly path: '/agent' | '/tickets' | '/supervise' | '/admin';
  /** The i18n key under `nav.` for this area's name. */
  readonly labelKey: string;
  readonly permission: string;
}

/** A safe path inside the workspace, so a `redirect` search parameter cannot send someone away. */
export function safeReturnPath(candidate: string | undefined): string {
  if (candidate === undefined || !candidate.startsWith('/') || candidate.startsWith('//')) {
    return '/';
  }
  return candidate;
}

export const workspaceAreas: readonly WorkspaceArea[] = [
  { id: 'agent', path: '/agent', labelKey: 'nav.agent', permission: 'agents.activity' },
  // Beside the agent's own screen rather than inside it: a ticket outlives the conversation it
  // came from, and the person looking one up is often not the person who raised it.
  { id: 'tickets', path: '/tickets', labelKey: 'nav.tickets', permission: 'tickets.read' },
  {
    id: 'supervise',
    path: '/supervise',
    labelKey: 'nav.supervise',
    permission: 'agents.supervise',
  },
  { id: 'admin', path: '/admin', labelKey: 'nav.admin', permission: 'config.read' },
];

/** The areas this principal may enter, in the order the navigation shows them. */
export function areasFor(principal: Principal): readonly WorkspaceArea[] {
  return workspaceAreas.filter((area) => holdsPermission(principal, area.permission));
}

/**
 * Where a person lands when they ask for the workspace itself.
 *
 * The first area they can enter, which makes an agent land in the workspace and an administrator in
 * configuration. `undefined` for somebody with no area at all — an auditor, say — who is shown what
 * they can do instead of being bounced between two routes that each refuse them.
 */
export function homePathFor(principal: Principal): WorkspaceArea['path'] | undefined {
  return areasFor(principal)[0]?.path;
}

/**
 * The area a path belongs to, which is what the navigation marks as current.
 *
 * Mostly the area whose path it starts with. The exception is `/settings`, which holds support
 * access: an administration screen with its own route because its permission is not the area's
 * (see `AdministrationFrame`), reached from the administration sidebar — so it is administration
 * that is current there, not nothing.
 */
export function areaAt(pathname: string): WorkspaceArea | undefined {
  const within = (path: string) => pathname === path || pathname.startsWith(`${path}/`);
  if (within('/settings')) {
    return workspaceAreas.find((area) => area.id === 'admin');
  }
  return workspaceAreas.find((area) => within(area.path));
}
